<template>
    <div class="contact-journey-steps-widget">
        <div class="widget-header">
            <h3>Contact Journey</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="loading-container" v-if="loading">
                <loading-screen-svg />
            </div>
            <div v-else>
                <div class="content-title">
                    <div>
                        <span @click="goToContact()" class="contact-link">
                            <img class="profile-image" :src="contact.profileImageUrl | profileImagePlaceholder" /> {{ contact.fullName }}
                        </span> 
                        Steps Taken
                    </div>
                    <journey-status-indicator :status="status" />
                </div>
                
                <journey-action 
                    v-for="action in actions"
                    :key="action.id"
                    :journeyAction="action" />
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget()">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button class="save-btn" @click="viewJourney()">
                    View Journey
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const JourneyAction = () => import('@/components/journey/journey-action');
const JourneyStatusIndicator = () => import('@/components/journey/journey-status-indicator');

import EventBus from '@/scripts/event-bus.js';
import log from '@/scripts/logger';

export default {
    name: 'contact-journey-steps-widget',

    props: {
        widget: {
            type: Object,
            required: true
        },
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'journey-action': JourneyAction,
        'journey-status-indicator': JourneyStatusIndicator,
    },

    data() {
        return {
            loading: true,

            journeyId: this.widget.journeyId,
            contact: {
                id: this.widget.contactId,
                clientId: null,
                profileImageUrl: null,
                fullName: null
            },

            steps: [],
            actions: [],
            campaigns: [],
            users: [],
            contactStatuses: [],
            lists: []
        }
    },

    computed: {
        status() {
            if (this.actions.some(a => !a.occurred)) {
                return 'active';
            }
            else {
                var unsubscribed = this.actions.length > 0 && this.actions.some(a => a.unsubscribed);
                var lastAction = this.getLastAction();
                var hasChildStep = lastAction.step.type !== 'CONDITION' && lastAction.step.trueChildStepId;
                
                if (hasChildStep && unsubscribed) {
                    return 'unsubscribed';
                }
                else if (hasChildStep) {
                    return 'stopped';
                }
                else {
                    return 'completed';
                }
            }
        }
    },

    methods: {
        getLastAction() {
            return this.actions[this.actions.length - 1];
        },

        viewJourney() {
            window.open(`/Journeys/${ this.journeyId }/Design`);
        },

        goToContact() {
            this.$router.push(`/Contacts/${ this.contact.id }`);
            this.closeWidget();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Contacts(${ this.contact.id })?$select=ProfileImageUrl,FirstName,LastName,ClientId`)
                    .then(response => {
                        var contactResponse = response.data;

                        this.contact.clientId = contactResponse.ClientId;
                        this.contact.profileImageUrl = contactResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
                        this.contact.fullName = this.$helpers.getName(contactResponse.FirstName, contactResponse.LastName);
                        
                        resolve();
                    })
                    .catch((error) => {
                        const message = 'An error occurred while fetching your contact';
                        log.error(message, error);
                        reject(message);
                    }); 
            });
        },

        getActions() {
            var selectParam = `$select=Id,ActionDate,MessageId,Occurred,JourneyStepId`;
            var expandParam = `$expand=Message($select=Opens,Clicks,Unsubscribed),UserTask($select=AssignedUser;$expand=AssignedUser($select=FirstName,LastName))`;
            var filterParam = `$filter=ContactId eq ${ this.contact.id } and JourneyId eq ${ this.journeyId } and JourneyStepId ne null`;
            var orderParam = `$orderby=ActionDate`;

            var query = `/service/JourneyActions?${ selectParam }&${ expandParam }&${ filterParam }&${ orderParam }&$count=true`;

            this.$api
                .get(query)
                .then(response => {
                    this.actions = response.data.value.map(this.mapAction);
                    this.actions.forEach(action => {
                        action.conditionalPath = this.getConditionalPath(action);
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your opens.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });  
        },

        mapAction(response) {
            var opens = 0;
            var clicks = 0;
            var unsubscribed = false;
            var assignedUser = '';

            if (response.MessageId) {
                opens = response.Message.Opens;
                clicks = response.Message.Clicks;
                unsubscribed = response.Message.Unsubscribed;
            }

            if (response.UserTask) {
                assignedUser = response.UserTask.AssignedUser ? this.$helpers.getName(response.UserTask.AssignedUser.FirstName, response.UserTask.AssignedUser.LastName) : 'no one';
            }

            var step = this.steps.find(s => s.id === response.JourneyStepId);

            return {
                id: response.Id,
                occurred: response.Occurred,
                actionDate: this.$moment.utc(response.ActionDate).local(),
                messageId: response.MessageId,
                contactId: this.contact.id,
                opens: opens,
                clicks: clicks,
                unsubscribed: unsubscribed,
                assignedUser: assignedUser,
                conditionalPath: true,
                step: step
            };
        },

        constructJoinedConditionalStatement(rules, isConditionUnion) {
            var conditionalStatement = '';

            if (rules.length > 0) {
                var operator = isConditionUnion ? ' OR ' : ' AND ';
                conditionalStatement = rules
                    .map(rule => this.constructConditionalStatement(rule))
                    .join(operator);
            }
            
            return conditionalStatement;
        },

        constructContactUpdateStatement(step) {
            var statement = '';

            if (step.type === 'CONTACT UPDATE') {
                switch(step.updateType) {
                    case 'ContactStatus':
                        var match = this.contactStatuses.find(u => u.id == step.updateValue);
                        statement = `Change Contact Status to <span class="highlighted">${ match ? match.name : 'No Status' }</span>`;
                        break;
                    case 'AddToList':
                        var match = this.lists.find(u => u.id == step.updateValue);
                        statement = `Add to list <span class="highlighted">${ match ? match.name : 'No list assigned' }</span>`;
                        break;
                    case 'RemoveFromList':
                        var match = this.lists.find(u => u.id == step.updateValue);
                        statement = `Remove from list <span class="highlighted">${ match ? match.name : 'No list assigned' }</span>`;
                        break;
                    case 'AssignSalesRep':
                        if (step.updateValue === 'CurrentRep') {
                            statement = `Assign to current sales rep`;
                        }
                        else {
                            var match = this.users.find(u => u.id == step.updateValue);
                            if (match) {
                                statement = `Assign to <span class="highlighted">${ match.name }</span>`;
                            }
                            else {
                                statement = `Unassign sales rep from contact`;
                            }
                        }
                        break;
                }
            }

            return statement;
        },

        constructConditionalStatement(rule) {
            var target = this.getTarget(rule);
            var condition = rule.condition;
            var value = rule.value;

            if (condition === 'is provided') {
                condition = 'is defined';
            }

            var statement = `<span class="highlighted">${ target }</span> ${ condition }`;
            if (value) {
                statement += ` <span class="highlighted">${ value }</span>`;
            }

            return statement;
        },

        getTarget(rule) {
            var target = rule.target;

            if (rule.typeId === 2) { // Campaign Activity
                var matchingCampaign = this.campaigns.find(c => c.id === parseInt(rule.target));
                target = matchingCampaign ? matchingCampaign.name : 'No campaign name';
            }
            else if (rule.typeId === 3) { // Journey Activity
                var matchingStep = this.steps.find(s => s.id === parseInt(rule.target))
                target = matchingStep ? matchingStep.name : 'No step name';
            }

            return target;
        },

        getType(typeId) {
            switch(typeId) {
                case 1:
                    return 'DELAY';
                case 2:
                    return 'EMAIL';
                case 3:
                    return 'CONDITION';
                case 4:
                    return 'CONTACT UPDATE';
                case 5:
                    return 'TASK';
                default:
                    return null;
            }
        },

        getCampaigns() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=ClientId eq ${ this.contact.clientId }`;
                var select = `$select=Id,Name`;
                var restUrl = `/service/Campaigns?${ select }&${ filter }`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.campaigns = response.data.value.map(item => {
                            return {
                                id: item.Id,
                                name: item.Name
                            };
                        });

                        resolve();
                    })
                    .catch(reject);
            });
        },

        getConditionalPath(action) {
            if (action.step.type === 'CONDITION') {
                var hasTrueChildAction = this.actions.some(a => a.step.id === action.step.trueChildStepId);
                if (hasTrueChildAction) {
                    return true;
                }
            }

            return false;
        },

        getSteps() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=JourneyId eq ${ this.journeyId }`;
                var select = `$select=Id,TrueChildStepId,FalseChildStepId,Name,JourneyStepTypeId,DelayDuration,DelayDurationType,PreviewImageUrl,IsConditionUnion,UpdateType,UpdateValue`;
                var expand = `$expand=JourneyConditionRules($select=JourneyConditionRuleTypeId,Target,Condition,Value)`;
                var restUrl = `/service/JourneySteps?${ select }&${ filter }&${ expand }`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.steps = response.data.value.map(item => {
                            var rules = [];
                
                            if (item.JourneyConditionRules) {
                                rules = item.JourneyConditionRules.map(rule => {
                                    return {
                                        typeId: rule.JourneyConditionRuleTypeId,
                                        target: rule.Target,
                                        condition: rule.Condition,
                                        value: rule.Value
                                    };
                                });
                            } 

                            return {
                                id: item.Id,
                                previewImageUrl: item.PreviewImageUrl,
                                name: item.Name,
                                isConditionUnion: item.IsConditionUnion,
                                type: this.getType(item.JourneyStepTypeId),
                                delayDuration: item.DelayDuration,
                                delayDurationType: item.DelayDurationType,
                                updateType: item.UpdateType,
                                updateValue: item.UpdateValue,
                                trueChildStepId: item.TrueChildStepId,
                                falseChildStepId: item.FalseChildStepId,
                                rules: rules,
                                conditionalStatement: '',
                                contactUpdateStatement: ''
                            };
                        });

                        this.steps.forEach(step => {
                            step.conditionalStatement = this.constructJoinedConditionalStatement(step.rules, step.isConditionUnion);
                            step.contactUpdateStatement = this.constructContactUpdateStatement(step);
                        });

                        resolve();
                    })
                    .catch(reject);
            });
        },

        getContactStatuses() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=ClientId eq ${ this.contact.clientId }`;
                var restUrl = `/service/ContactStatuses/?${ filter }&$select=Name,Id`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.contactStatuses = response.data.value.map(item => {
                            return {
                                id: item.Id,
                                name: item.Name
                            };
                        });

                        resolve();
                    })
                    .catch((error) => {
                        reject();
                        this.$helpers.showFailNotification('An error occurred while fetching your contact statuses.', { error });
                    });
            })
        },

        getLists() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=ClientId eq ${ this.contact.clientId }`;
                var restUrl = `/service/Lists/?${ filter }&$select=Name,Id`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.lists = response.data.value.map(item => {
                            return {
                                id: item.Id,
                                name: item.Name
                            };
                        });

                        resolve();
                    })
                    .catch((error) => {
                        reject();
                        this.$helpers.showFailNotification('An error occurred while fetching your lists.', { error });
                    });
            })
        },

        getUsers() {
            return new Promise((resolve, reject) => {
                var filter = `$filter=ClientId eq ${ this.contact.clientId }`;
                var select = `$select=Id,FirstName,LastName`;
                var restUrl = `/service/Users?${ filter }&${ select }`;

                this.$api
                    .get(restUrl)
                    .then(response => {
                        this.users = response.data.value.map(item => {
                            return { 
                                id: item.Id,
                                name: this.$helpers.getName(item.FirstName, item.LastName)
                            };
                        });

                        resolve();
                    })
                    .catch(reject);
            });
        },
    },

    created() {
        this.getContact()
            .then(() => {
                return Promise.all([
                    this.getUsers(),
                    this.getCampaigns(),
                    this.getLists(),
                    this.getContactStatuses()
                ]);
            })
            .then(this.getSteps)
            .then(this.getActions);
    },

}
</script>

<style>
.contact-journey-steps-widget .content-title {
    font: bold 20px/1em 'Nunito';
    border-bottom: 2px solid #E9E9EF;
    padding-bottom: 20px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.contact-journey-steps-widget .contact-link {
    color: #18AEFF;
    cursor: pointer;
}

.contact-journey-steps-widget .content-title .profile-image {
    height: auto;
    max-height: 100%;
    border-radius: 50%;
    max-width: 32px;
    margin-right: 8px;
}

.contact-journey-steps-widget.widget .content-wrapper {
    padding-top: 25px;
}

.contact-journey-steps-widget .action-container {
    display: flex;
}

.contact-journey-steps-widget .action-container .btn {
    margin-left: 15px;
}

.contact-journey-steps-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877D88;
}

.contact-journey-steps-widget .cancel-container .btn.transparent-btn:hover {
    background: #E1E0E5;
}

.contact-journey-steps-widget .action-container .btn.white-btn {
    background: #FFFFFF;
    color: #877D88;
    border: 2px solid #E9E9EF;
}
</style>
